import { useSidebarStore } from '@/stores/sidebar-store';
import { useTranslation } from '@kgc/i18n';
import { useNavigate } from 'react-router-dom';
import { DASHBOARD_NAV_ITEM, NAV_GROUPS, type NavItem } from './nav-config';

/**
 * SidebarOverlay — Tablet + mobil hamburger overlay panel
 * Slide-in animáció balról, backdrop tap → bezár
 * @see tech-spec-nav-ux-overhaul.md F3-T4
 */
export function SidebarOverlay() {
  const { overlayOpen, closeOverlay } = useSidebarStore();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleNavigate = (href: string) => {
    navigate(href);
    closeOverlay();
  };

  const renderItem = (item: NavItem) => {
    if (item.href) {
      return (
        <button
          key={item.id}
          onClick={() => handleNavigate(item.href!)}
          className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-[#1e4a52] dark:text-white/80 transition-colors hover:bg-black/5 dark:hover:bg-white/10"
        >
          <div className="nav-icon-badge" style={{ backgroundColor: item.iconColor ?? '#64748b' }}>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="#fff"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d={item.icon} />
            </svg>
          </div>
          <span>{t(item.label)}</span>
        </button>
      );
    }

    return (
      <div key={item.id}>
        <div className="flex items-center gap-3 px-4 py-2">
          <div className="nav-icon-badge" style={{ backgroundColor: item.iconColor ?? '#64748b' }}>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="#fff"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d={item.icon} />
            </svg>
          </div>
          <span className="text-sm font-medium text-[#1e4a52] dark:text-white/80">
            {t(item.label)}
          </span>
        </div>
        {item.children && (
          <div className="bg-black/[0.03] dark:bg-black/20">
            {item.children.map(child => (
              <button
                key={child.href}
                onClick={() => handleNavigate(child.href)}
                className="flex w-full items-center py-2 pl-14 pr-4 text-left text-sm text-[#3d6b73] dark:text-white/80 transition-colors hover:bg-black/[0.03] hover:text-[#1e4a52] dark:hover:text-white"
              >
                {t(child.label)}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Backdrop */}
      {overlayOpen && (
        <div
          className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40"
          onClick={closeOverlay}
          aria-hidden="true"
        />
      )}

      {/* Panel — A11y: inert when closed prevents focus trap + aria-hidden violation.
           Inert automatically removes children from focus + a11y tree when panel is offscreen. */}
      <div
        className={`fixed left-0 top-0 bottom-0 w-[260px] z-50 sidebar-bg flex flex-col ${!overlayOpen ? 'pointer-events-none' : ''}`}
        style={{
          transform: overlayOpen ? 'translateX(0)' : 'translateX(-100%)',
          transition: 'transform 200ms ease-out',
        }}
        role="dialog"
        aria-modal="true"
        aria-label={t('nav.sidebarMenu')}
        {...(!overlayOpen && { inert: '' as unknown as boolean })}
      >
        {/* Header */}
        <div className="flex h-16 items-center justify-between border-b border-[#b8d8dd] dark:border-white/10 px-4">
          <div className="flex items-center gap-2">
            <img src="/rpi-logo.png" alt="rPI ERP" className="h-8 w-auto dark:invert" />
            <span className="font-semibold text-[#1e4a52] dark:text-white">alpha v4.0</span>
          </div>
          <button
            onClick={closeOverlay}
            className="rounded p-1 transition-colors hover:bg-black/5 dark:hover:bg-white/10 text-[#1e4a52] dark:text-white"
            aria-label="Bezárás"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-2">
          {renderItem(DASHBOARD_NAV_ITEM)}
          {NAV_GROUPS.map((group, idx) => (
            <div key={group.groupLabel ?? idx}>
              {group.groupLabel && (
                <div className="mt-3 mb-1 border-t border-[#b8d8dd]/60 dark:border-white/10 pt-2 px-4">
                  <span className="text-[9px] font-semibold uppercase tracking-widest text-[#5a8a92] dark:text-white/60">
                    {t(group.groupLabel)}
                  </span>
                </div>
              )}
              {group.items.map(item => renderItem(item))}
            </div>
          ))}
        </nav>
      </div>
    </>
  );
}
